import {GeneralSaleTransactionType, TrustPurpose} from '@sollapay/enums';

import {generalSaleOptionTrustType} from './general-sale-options.trust-type';
import {generalSaleSecondHandTrustType} from './general-sale-second-hand.trust-type';
import {getAllAssetPropertyDocumentTypes} from './trust-type.strategy';

import type {TrustTypeStrategy} from './trust-type.strategy';
import type {DocumentType} from '@sollapay/enums';

/** Exhaustive by design — a new transaction type is a build error until it picks a shape. */
const GENERAL_SALE_STRATEGIES: Record<GeneralSaleTransactionType, TrustTypeStrategy> = {
  [GeneralSaleTransactionType.OPTION_ASSIGNMENT]: generalSaleOptionTrustType,
  [GeneralSaleTransactionType.SECOND_HAND_SALE]: generalSaleSecondHandTrustType
};

/** An account that hasn't answered the transaction-type question yet behaves as second-hand. */
export function getGeneralSaleTrustType(
  transactionType: GeneralSaleTransactionType | null
): TrustTypeStrategy {
  return GENERAL_SALE_STRATEGIES[transactionType ?? GeneralSaleTransactionType.SECOND_HAND_SALE];
}

/** Every required-or-optional asset-property document type any general-sale transaction type
 * accepts — computed once, since the strategy set behind it never changes at runtime. */
const GENERAL_SALE_ASSET_PROPERTY_DOCUMENT_TYPES = new Set(
  Object.values(GENERAL_SALE_STRATEGIES).flatMap(getAllAssetPropertyDocumentTypes)
);

/**
 * Document types that were valid asset-property uploads under a transaction type this account no
 * longer has — e.g. an option-agreement upload survives after the account is switched from
 * option assignment to second-hand. `[]` for every trust type without a switchable sub-variant,
 * so a caller can call this unconditionally regardless of the account's trust purpose.
 */
export function getStaleAssetPropertyDocumentTypes(strategy: TrustTypeStrategy): DocumentType[] {
  if (strategy.purpose !== TrustPurpose.GENERAL_SALE) return [];
  const current = new Set(getAllAssetPropertyDocumentTypes(strategy));
  return [...GENERAL_SALE_ASSET_PROPERTY_DOCUMENT_TYPES].filter(type => !current.has(type));
}
